import FormSwiftAPIClient from 'FormSwiftAPI/FormSwiftAPIClient';
import { gracefulSave } from 'FormSwiftAPI/Session/SessionActions';
import { startGracefulAutoSave } from 'FormSwiftAPI/Session/SessionAutoSaveActions';
import getDocumentContent from 'FormSwiftAPI/Session/DocumentContent';
import { uuidV4 } from 'Utils';
import { ImageTypes } from 'Popups/PopupConstants';
import FreeWriteBuilderEnum from './FreeWriteBuilderConstants';

const formSwiftAPIClient = new FormSwiftAPIClient(window.API_URL);

export const getDocumentShareContentToSave = () => (
  (dispatch, getState) => {
    const state = getState();
    const documentContent = getDocumentContent(state);
    return {
      shareKey: window.DocumentSettings.shareKey,
      payload: {
        content: documentContent,
      },
    };
  }
);

export const saveDocumentShareContent = ({ shareKey, payload }) => (
  async () => {
    await formSwiftAPIClient.request('POST', `/document-share/session/content/${shareKey}`, payload);
    return payload;
  }
);

export const getDocumentContentToSave = () => (
  (dispatch, getState) => {
    const state = getState();
    const documentContent = getDocumentContent(state);
    return {
      sessionId: window.DocumentSettings.key,
      payload: {
        content: documentContent,
        session_hash: window.DocumentSettings.ses,
        user_id: window.COOKIE.get('userid'),
        user_token: window.COOKIE.get('userToken'),
      },
    };
  }
);

export const saveDocumentContent = ({ sessionId, payload }) => (
  async () => {
    await formSwiftAPIClient.request('POST', `/session/content/${sessionId}`, payload);
    return payload;
  }
);

export const saveFreeWriteContent = () => (
  async (dispatch) => {
    const { shareKey } = window.DocumentSettings;
    if (shareKey && shareKey.length > 0) {
      const savingArgs = dispatch(getDocumentShareContentToSave());
      await dispatch(gracefulSave(saveDocumentShareContent, savingArgs));
      return;
    }

    const savingArgs = dispatch(getDocumentContentToSave());
    await dispatch(gracefulSave(saveDocumentContent, savingArgs));
  }
);

export const startFreeWriteAutoSave = () => (
  (dispatch) => {
    const { shareKey } = window.DocumentSettings;
    if (shareKey && shareKey.length > 0) {
      const initialAutoSaveValue = dispatch(getDocumentShareContentToSave());
      dispatch(startGracefulAutoSave(
        getDocumentShareContentToSave,
        saveDocumentShareContent,
        initialAutoSaveValue,
        3000,
      ));
      return;
    }

    const initialAutoSaveValue = dispatch(getDocumentContentToSave());
    dispatch(startGracefulAutoSave(
      getDocumentContentToSave,
      saveDocumentContent,
      initialAutoSaveValue,
      3000,
    ));
  }
);

const getPopupsFromUserData = (userData, builderOffset) => (
  (dispatch, getState) => {
    const state = getState();
    const minPopupZIndex = 3;
    const isRequestSignatureExperience = state.EditorStateHandler.isRequestSignatureExperience;

    const popups = {};
    userData.forEach((data) => {
      const id = uuidV4();
      const offsetX = builderOffset.adjust.x;
      const offsetY = builderOffset.adjust.y;
      const x = (parseInt(data.position.x.slice(0, -2) * builderOffset.scale, 10) + offsetX);
      const y = (parseInt(data.position.y.slice(0, -2) * builderOffset.scale, 10) + offsetY);

      if (data.type === 'text') {
        const width = parseInt(data.width, 10);
        const height = parseInt(data.height, 10);
        const pageIdx = parseInt(data['page-idx'], 10);

        const text = data.value.replace(/<br\s?\/?>/g, '\n');
        const bold = data['font-weight'] === 'bold' || data['font-weight'] === '700';
        const italic = data['font-style'] === 'italic';
        const fontFamily = data['font-type'];
        const fontSize = parseInt(data['font-size'], 10);
        const color = data['font-color'];
        const { backgroundColor, signer } = data;
        const eSignSettings = data.eSignSettings ? {
          eSignSettings: data.eSignSettings,
        } : {};

        if (data['request-type']) {
          if (!isRequestSignatureExperience) {
            // if we're not in request mode, we shouldn't populate share/request fields
            return;
          }
          const requestType = data['request-type'];
          const draggable = false;
          const disabled = true;
          const resizable = false;
          const requestSettings = {};
          const contentSettings = {};

          contentSettings.type = data.type;
          contentSettings.text = text;
          contentSettings.bold = bold;
          contentSettings.italic = italic;
          contentSettings.fontFamily = fontFamily;
          contentSettings.fontSize = fontSize;
          contentSettings.color = color;

          switch (data['request-type']) {
            case 'text':
              requestSettings.title = 'Text';
              requestSettings.address = signer;
              contentSettings.placeholderText = 'Text';
              break;
            case 'date':
              requestSettings.title = 'Date';
              requestSettings.address = signer;
              contentSettings.placeholderText = 'Date';
              break;
            default:
              // invalid request popup
              return;
          }

          popups[id] = {
            contentSettings,
            disabled,
            draggable,
            height,
            id,
            pageIdx,
            requestSettings,
            requestType,
            resizable,
            type: 'request-signature',
            width,
            x,
            y,
            zIndex: userData.length + minPopupZIndex,
          };
        } else {
          popups[id] = {
            id,
            type: 'text',
            x,
            y,
            width,
            height,
            pageIdx,
            text,
            bold,
            italic,
            fontFamily,
            fontSize,
            color,
            draggable: true,
            disabled: false,
            zIndex: data['z-index'],
            backgroundColor,
            ...eSignSettings,
          };
        }
      } else if (data.type === 'checkbox') {
        const width = parseInt(data.width, 10);
        const height = parseInt(data.height, 10);
        const pageIdx = parseInt(data['page-idx'], 10);

        popups[id] = ({
          id,
          type: 'checkbox',
          draggable: false,
          x,
          y,
          width,
          height,
          pageIdx,
          checked: data.checked === true,
          disabled: false,
          zIndex: data['z-index'],
        });
      } else if (data.type === 'image') {
        const imgSrc = data['img-src'];
        const imgIdx = data['img-idx'];
        const imgType = data['img-type'];
        const pageIdx = parseInt(data['page-idx'], 10);
        const width = parseInt(data.width, 10);
        const eSignSettings = data.eSignSettings ? {
          eSignSettings: data.eSignSettings,
        } : {};

        if (data.request) {
          if (!isRequestSignatureExperience) {
            // if we're not in request mode, we shouldn't populate share/request fields
            return;
          }

          const height = parseInt(data.height, 10);
          const requestType = 'sign';
          const draggable = false;
          const disabled = true;
          const resizable = false;
          const requestSettings = {};
          const contentSettings = {};

          contentSettings.type = data.type;
          contentSettings.imageUrl = imgSrc;
          contentSettings.imgIdx = imgIdx;
          contentSettings.imgType = ImageTypes.SIGNATURE;
          requestSettings.title = 'Click to Sign';

          requestSettings.address = data.signer;

          popups[id] = {
            id,
            type: 'request-signature',
            requestType,
            x,
            y,
            width,
            height,
            pageIdx,
            draggable,
            disabled,
            zIndex: userData.length + minPopupZIndex,
            resizable,
            requestSettings,
            contentSettings,
          };
        } else {
          popups[id] = ({
            id,
            type: 'image',
            imageUrl: imgSrc,
            imgIdx,
            imgType,
            pageIdx,
            width,
            x,
            y,
            draggable: true,
            disabled: false,
            zIndex: data['z-index'],
            ...eSignSettings,
          });
        }
      } else if (['eraser', 'highlighter', 'redact'].indexOf(data.type) > -1) {
        popups[id] = ({
          id,
          type: data.type,
          imageUrl: `/free-write/assets/img/${data.type}.png`,
          imgIdx: parseInt(data['img-idx'], 10),
          pageIdx: data['page-idx'],
          width: parseInt(data.width, 10),
          height: parseInt(data.height, 10),
          x,
          y,
          draggable: true,
          disabled: false,
          hovering: false,
          resizable: true,
        });
      } else if (data.type === 'logo') {
        popups[id] = ({
          id,
          type: 'logo',
          imageUrl: data['img-src'],
          imgIdx: data['img-idx'],
          width: parseInt(data.width, 10),
          x,
          y,
          draggable: true,
          disabled: false,
          isGlobal: true,
        });
      }
    });

    return popups;
  }
);

export const loadDocumentContent = documentContent => (
  (dispatch) => {
    dispatch({
      type: FreeWriteBuilderEnum.FREE_WRITE_BUILDER_DOCUMENT_CONTENT_GET_REQUEST,
    });

    try {
      if (documentContent.length === 0) return;
      if (documentContent['form-id'] && String(documentContent['form-id']) !== String(window.FORM_ID)) return;

      // Adjust placement of content if users came in through an older version of the builder
      const builderOffset = {
        adjust: {
          x: 0,
          y: 0,
        },
        scale: 1,
      };

      // The older builder had a different scale for the pages
      if (!documentContent.builderVersion) {
        // The old builder had margins and padding, we'll adjust the position for that
        builderOffset.adjust.x = -15;
        builderOffset.adjust.y = -5;
      }
      const userData = documentContent['user-data'] || [];
      const popups = dispatch(getPopupsFromUserData(userData, builderOffset));

      dispatch({
        type: FreeWriteBuilderEnum.FREE_WRITE_BUILDER_DOCUMENT_CONTENT_GET_SUCCESS,
        data: { popups },
      });

      dispatch(startFreeWriteAutoSave());
    } catch (e) {
      dispatch({
        type: FreeWriteBuilderEnum.FREE_WRITE_BUILDER_DOCUMENT_CONTENT_GET_FAILURE,
        error: e,
      });
    }
  }
);



// WEBPACK FOOTER //
// ./src-react/FreeWriteBuilder/FreeWriteBuilderActions.jsx